import { useEffect, useState } from "react"
import { Navigate, useSearchParams } from "react-router-dom"
import { motion } from "motion/react"
import { Check, Terminal, ArrowRight, Loader2, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import { AuthLayout } from "@/components/auth-layout"
import { AuthCard, AuthHeading, LogoMark } from "@/components/auth-card"
import { useSession } from "@/hooks/use-session"

type Phase = "loading" | "ready" | "approved" | "denied" | "error"

export function CliAuthorizePage() {
  const session = useSession()
  const [params] = useSearchParams()
  const code = params.get("code") || ""

  const [phase, setPhase] = useState<Phase>("loading")
  const [userCode, setUserCode] = useState(code.toUpperCase())
  const [client, setClient] = useState("")
  const [error, setError] = useState("")
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (session.status !== "authenticated") return
    if (!code) return
    let cancelled = false
    fetch(`/api/v1/saas/auth/cli/info?code=${encodeURIComponent(code)}`, {
      credentials: "include",
    })
      .then(async (res) => {
        if (cancelled) return
        const body = (await res.json()) as {
          user_code?: string
          client?: string
          error?: string
        }
        if (!res.ok) {
          setPhase("error")
          setError(humanize(res.status, body.error))
          return
        }
        setUserCode(body.user_code || code.toUpperCase())
        setClient(body.client || "")
        setPhase("ready")
      })
      .catch(() => {
        if (cancelled) return
        setPhase("error")
        setError("Couldn't reach the server. Try again in a moment.")
      })
    return () => {
      cancelled = true
    }
  }, [session.status, code])

  const post = async (path: string) => {
    setBusy(true)
    try {
      const res = await fetch(path, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      })
      const body = (await res.json()) as { error?: string }
      return { ok: res.ok, status: res.status, error: body.error }
    } catch {
      return { ok: false, status: 0, error: "network" }
    } finally {
      setBusy(false)
    }
  }

  const authorize = async () => {
    const r = await post("/api/v1/saas/auth/cli/authorize")
    if (r.ok) setPhase("approved")
    else {
      setPhase("error")
      setError(humanize(r.status, r.error))
    }
  }

  const cancel = async () => {
    await post("/api/v1/saas/auth/cli/deny")
    setPhase("denied")
  }

  if (session.status === "loading") return null
  if (session.status === "unauthenticated") return <Navigate to="/login" replace />

  const effectivePhase: Phase = !code ? "error" : phase
  const effectiveError = !code
    ? "This link is missing its code. Run hivehook login again."
    : error

  return (
    <AuthLayout>
      <LogoMark />
      <AuthCard>
        {effectivePhase === "approved" ? (
          <Approved />
        ) : effectivePhase === "denied" ? (
          <Closed
            icon={<X className="h-7 w-7 text-muted-foreground" strokeWidth={2.5} />}
            title="Authorization cancelled"
            body="No access was granted. You can close this tab."
          />
        ) : effectivePhase === "error" ? (
          <Closed
            icon={<X className="h-7 w-7 text-muted-foreground" strokeWidth={2.5} />}
            title="Something's off"
            body={effectiveError}
          />
        ) : effectivePhase === "loading" ? (
          <div className="flex h-48 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/60" />
          </div>
        ) : (
          <div className="space-y-7">
            <div className="flex flex-col items-start gap-5">
              <HexGlyph>
                <Terminal className="h-6 w-6 text-rose-400" strokeWidth={1.75} />
              </HexGlyph>
              <AuthHeading
                eyebrow="Command line"
                title="Authorize the CLI"
                description="A device wants to sign in to your Hivehook account from the terminal."
              />
            </div>

            <div className="space-y-2">
              <p className="text-2xs uppercase tracking-[0.18em] text-muted-foreground/60">
                Confirm this matches your terminal
              </p>
              <div className="rounded-xl border border-rose-500/25 bg-rose-500/5 px-4 py-5 text-center">
                <span className="font-mono text-3xl font-semibold tracking-[0.3em] text-foreground">
                  {userCode}
                </span>
              </div>
            </div>

            <dl className="space-y-2.5 border-t border-border/40 pt-5 text-sm">
              <Row label="Account" value={session.user.email} />
              <Row label="Request" value={client ? `just now · ${client}` : "just now"} />
            </dl>

            <div className="space-y-3">
              <Button
                onClick={authorize}
                disabled={busy}
                className="group w-full bg-rose-600 text-white shadow-lg shadow-rose-500/20 transition-all hover:bg-rose-500 hover:shadow-rose-500/30 disabled:bg-rose-600/60"
              >
                {busy ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Authorizing
                  </>
                ) : (
                  <>
                    Authorize
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </Button>
              <Button
                variant="ghost"
                onClick={cancel}
                disabled={busy}
                className="w-full text-muted-foreground hover:text-foreground"
              >
                Cancel
              </Button>
            </div>

            <p className="text-center text-2xs leading-relaxed text-muted-foreground/60">
              Only authorize if you just ran{" "}
              <code className="font-mono text-muted-foreground/80">hivehook login</code>{" "}
              yourself.
            </p>
          </div>
        )}
      </AuthCard>
    </AuthLayout>
  )
}

function humanize(status: number, raw?: string): string {
  if (raw && status !== 0) return raw
  return "Couldn't reach the server. Try again in a moment."
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-2xs uppercase tracking-[0.18em] text-muted-foreground/60">
        {label}
      </dt>
      <dd className="truncate font-mono text-sm text-foreground">{value}</dd>
    </div>
  )
}

function Approved() {
  return (
    <Closed
      icon={<Check className="h-7 w-7 text-rose-400" strokeWidth={2.5} />}
      title="You're all set"
      body="Return to your terminal, the CLI is now signed in. You can close this tab."
      rose
    />
  )
}

function Closed({
  icon,
  title,
  body,
  rose,
}: {
  icon: React.ReactNode
  title: string
  body: string
  rose?: boolean
}) {
  return (
    <div className="space-y-7 text-center">
      <HexGlyph center muted={!rose}>
        {icon}
      </HexGlyph>
      <div className="space-y-1.5">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">{title}</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">{body}</p>
      </div>
    </div>
  )
}

function HexGlyph({
  children,
  center,
  muted,
}: {
  children: React.ReactNode
  center?: boolean
  muted?: boolean
}) {
  const stroke = muted ? "rgb(148 163 184 / 0.5)" : "rgb(244 63 94 / 0.6)"
  const top = muted ? "rgb(148 163 184 / 0.14)" : "rgb(244 63 94 / 0.18)"
  const bottom = muted ? "rgb(148 163 184 / 0.05)" : "rgb(244 63 94 / 0.06)"
  return (
    <motion.div
      initial={{ scale: 0.85, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className={center ? "relative mx-auto h-16 w-16" : "relative h-16 w-16"}
    >
      <svg viewBox="0 0 100 100" className="relative h-full w-full" aria-hidden>
        <defs>
          <linearGradient id="hexGlyphFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={top} />
            <stop offset="100%" stopColor={bottom} />
          </linearGradient>
        </defs>
        <path
          d="M50 6 L91 28 L91 72 L50 94 L9 72 L9 28 Z"
          fill="url(#hexGlyphFill)"
          stroke={stroke}
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">{children}</div>
    </motion.div>
  )
}
